import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './GuidesIndex.css';

const GuidesIndex = () => {
  const blogPosts = [
    {
      id: 'retatrutide-complete-guide',
      title: 'Retatrutide Complete Guide 2025',
      excerpt: 'Everything you need to know about Retatrutide (LY3437943) - the triple agonist showing 24% weight loss. Dosing protocols, clinical trials, and comparisons.',
      date: 'January 15, 2025',
      readTime: '15 min',
      category: 'GLP-1 Agonists',
      featured: true
    },
    {
      id: 'tirzepatide-vs-semaglutide',
      title: 'Tirzepatide vs Semaglutide: Complete Comparison',
      excerpt: 'Head-to-head comparison of Mounjaro and Ozempic/Wegovy. Which GLP-1 medication is right for you?',
      date: 'January 14, 2025',
      readTime: '12 min',
      category: 'Comparisons',
      featured: false
    },
    {
      id: 'glp1-side-effects',
      title: 'Managing GLP-1 Side Effects: A Practical Guide',
      excerpt: 'Learn how to minimize and manage common side effects from GLP-1 agonists like nausea, constipation, and fatigue.',
      date: 'January 13, 2025',
      readTime: '10 min',
      category: 'Safety',
      featured: false
    },
    {
      id: 'peptide-reconstitution',
      title: 'Peptide Reconstitution: Step-by-Step Guide',
      excerpt: 'Master the art of reconstituting peptides safely and accurately. Includes bacteriostatic water calculations.',
      date: 'January 12, 2025',
      readTime: '8 min',
      category: 'How-To',
      featured: false
    }
  ];

  // Structured data for blog listing
  const guidesStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "PeptideDose Guides",
    "description": "Expert guides on peptide calculators, GLP-1 agonists, and weight loss medications",
    "url": "https://peptidedose.com/guides",
    "publisher": {
      "@type": "Organization",
      "name": "PeptideDose"
    }
  };

  return (
    <div className="guides-index">
      <Helmet>
        <title>Peptide Guides - GLP-1 Tutorials, Retatrutide Guide & Dosing Tips | PeptideDose</title>
        <meta name="description" content="Expert guides on peptide dosing, GLP-1 agonists like Retatrutide and Tirzepatide, reconstitution tutorials, and weight loss medication comparisons." />
        <meta name="keywords" content="peptide blog, GLP-1 guides, retatrutide news, tirzepatide articles, peptide dosing guides" />
        
        <script type="application/ld+json">
          {JSON.stringify(guidesStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="guides-hero">
        <div className="hero-content">
          <Link to="/" className="back-link">← Back to Calculator</Link>
          <h1 className="guides-index-title">Peptide Knowledge Hub</h1>
          <p className="guides-index-subtitle">Expert guides on GLP-1 agonists, dosing protocols, and the latest research</p>
        </div>
      </div>

      {/* Featured Post */}
      <div className="featured-section">
        <div className="section-content">
          <h2 className="section-title">Featured Article</h2>
          {blogPosts.filter(post => post.featured).map(post => (
            <article key={post.id} className="featured-post">
              <div className="featured-content">
                <span className="post-category">{post.category}</span>
                <h3 className="featured-title">
                  <Link to={`/guides/${post.id}`}>{post.title}</Link>
                </h3>
                <p className="featured-excerpt">{post.excerpt}</p>
                <div className="post-meta">
                  <span className="post-date">{post.date}</span>
                  <span className="post-read-time">{post.readTime} read</span>
                </div>
                <Link to={`/guides/${post.id}`} className="read-more-btn">
                  Read Full Article →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* All Posts */}
      <div className="posts-section">
        <div className="section-content">
          <h2 className="section-title">Latest Articles</h2>
          <div className="posts-grid">
            {blogPosts.filter(post => !post.featured).map(post => (
              <article key={post.id} className="guides-post-card">
                <div className="post-card-content">
                  <span className="post-category">{post.category}</span>
                  <h3 className="post-title">
                    <Link to={`/guides/${post.id}`}>{post.title}</Link>
                  </h3>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <div className="post-meta">
                    <span className="post-date">{post.date}</span>
                    <span className="post-read-time">{post.readTime}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="categories-section">
        <div className="section-content">
          <h2 className="section-title">Browse by Category</h2>
          <div className="categories-grid">
            <Link to="/guides/category/glp1" className="category-card">
              <h3>GLP-1 Agonists</h3>
              <p>Semaglutide, Tirzepatide, Retatrutide guides</p>
            </Link>
            <Link to="/guides/category/how-to" className="category-card">
              <h3>How-To Guides</h3>
              <p>Reconstitution, dosing, and injection tutorials</p>
            </Link>
            <Link to="/guides/category/comparisons" className="category-card">
              <h3>Comparisons</h3>
              <p>Head-to-head medication comparisons</p>
            </Link>
            <Link to="/guides/category/safety" className="category-card">
              <h3>Safety & Side Effects</h3>
              <p>Managing side effects and safety protocols</p>
            </Link>
          </div>
        </div>
      </div>

      {/* Newsletter CTA */}
      <div className="newsletter-section">
        <div className="section-content">
          <h2>Stay Updated on Peptide Research</h2>
          <p>Get the latest news on GLP-1 agonists, clinical trials, and dosing protocols</p>
          <div className="cta-buttons">
            <Link to="/" className="cta-btn primary">
              Try Our Calculator
            </Link>
            <Link to="/faq" className="cta-btn secondary">
              Read FAQ
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidesIndex;